/**
 * PanelEwelink: presentacional puro del mantenedor de eWeLink (F4).
 *
 * Sin fetch: recibe todo por props para validarse en el preview dev-only
 * `/dev/ewelink` antes de existir en `sa.calbucura.cl`.
 *
 * Qué administra y qué NO:
 * - SÍ: qué dispositivos puede tocar el portal (`administrado`).
 * - NO: las credenciales. Correo, clave y región son variables de entorno
 *   del servidor (convención #17); acá solo se muestran para verificar
 *   contra qué cuenta se está trabajando. Ver
 *   `docs/dev/ewelink-variables-entorno.md`.
 *
 * El modo del backend va arriba y destacado a propósito: si está en
 * `simulacion`, los botones del portal de Tesorería NO ejecutan nada, y
 * quien administre esto tiene que saberlo antes de marcar dispositivos.
 */
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Table, TBody, THead, Th, Tr, Td } from '../ui/DataTable';

export type ModoEwelink = 'simulacion' | 'real' | 'falso';

export interface DispositivoEwelinkRead {
  id: string;
  deviceid: string;
  nombre: string;
  canal: string;
  es_virtual: boolean;
  administrado: boolean;
  limite_usuarios: number;
  /** Compartidos que eWeLink reporta ahora. null = no se pudo consultar. */
  usados: number | null;
}

export interface EstadoEwelink {
  conexion_ok: boolean;
  mensaje: string;
  region: string;
  modo: ModoEwelink;
  /** Correo de la cuenta, para verificar contra cuál se trabaja. */
  cuenta: string | null;
  consultado_at: string | null;
}

const CANAL_LABEL: Record<string, string> = {
  porton_entrada: 'Portón · entrada',
  porton_salida: 'Portón · salida',
  puerta_entrada: 'Puerta · entrada',
  puerta_salida: 'Puerta · salida',
  otro: 'Sin clasificar',
};

function AvisoModo({ modo }: { modo: ModoEwelink }) {
  if (modo === 'real') {
    return (
      <div className="rounded-md border border-state-danger bg-state-danger/10 p-3">
        <p className="text-sm font-bold text-state-danger">
          Modo REAL — el portal ejecuta de verdad
        </p>
        <p className="text-xs text-text-secondary mt-0.5">
          Las altas y bajas desde Tesorería se aplican en eWeLink al
          instante. Los vecinos ganan o pierden acceso a los portones.
        </p>
      </div>
    );
  }
  if (modo === 'falso') {
    return (
      <div className="rounded-md border border-line-strong bg-bg-page p-3">
        <p className="text-sm font-bold text-text-primary">
          Modo de pruebas — sin conexión a eWeLink
        </p>
        <p className="text-xs text-text-secondary mt-0.5">
          Este modo es solo para tests automatizados. No debería estar
          activo en producción.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-state-warning bg-state-warning/10 p-3">
      <p className="text-sm font-bold text-state-warning">
        Modo SIMULACIÓN — nada se ejecuta
      </p>
      <p className="text-xs text-text-secondary mt-0.5">
        El sistema lee el estado real de eWeLink, pero las altas y bajas
        desde Tesorería <strong>no se aplican</strong>. Es el modo seguro
        para revisar antes de operar. Se cambia con la variable{' '}
        <code className="font-mono">EWELINK_BACKEND</code> en Render, no
        desde acá.
      </p>
    </div>
  );
}

export function PanelEwelink({
  estado,
  dispositivos,
  onProbarConexion,
  onToggleAdministrado,
  cargando = false,
  guardando = null,
}: {
  estado: EstadoEwelink;
  dispositivos: DispositivoEwelinkRead[];
  onProbarConexion?: () => void;
  onToggleAdministrado?: (d: DispositivoEwelinkRead, valor: boolean) => void;
  cargando?: boolean;
  /** deviceid en curso de guardado, para deshabilitar solo esa fila. */
  guardando?: string | null;
}) {
  const fisicos = dispositivos.filter((d) => !d.es_virtual);
  const virtuales = dispositivos.filter((d) => d.es_virtual);
  const sinClasificar = dispositivos.filter((d) => d.canal === 'otro');
  const administrados = dispositivos.filter((d) => d.administrado).length;

  // Capacidad en VECINOS, no en cupos: cada vecino ocupa un lugar en todos
  // los canales a la vez, así que los físicos no son capacidades
  // independientes (ver doctrina §5).
  const fisAdm = fisicos.filter((d) => d.administrado);
  const virAdm = virtuales.filter((d) => d.administrado);
  const capacidad = fisAdm.length
    ? fisAdm[0].limite_usuarios *
      (1 + Math.floor(virAdm.length / fisAdm.length))
    : 0;

  const filas = (items: DispositivoEwelinkRead[]) =>
    items.map((d) => (
      <Tr key={d.id}>
        <Td>
          <span className="font-medium text-text-primary">{d.nombre}</span>
        </Td>
        <Td>
          <span className="font-mono text-xs text-text-secondary">
            {d.deviceid}
          </span>
        </Td>
        <Td>
          {d.canal === 'otro' ? (
            <Badge variant="warning">Sin clasificar</Badge>
          ) : (
            <span className="text-text-secondary">
              {CANAL_LABEL[d.canal] ?? d.canal}
            </span>
          )}
        </Td>
        <Td>
          <span className="tabular-nums text-text-secondary">
            {d.usados === null ? '—' : `${d.usados} / ${d.limite_usuarios}`}
          </span>
        </Td>
        <Td>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={d.administrado}
              disabled={guardando === d.deviceid}
              onChange={(e) => onToggleAdministrado?.(d, e.target.checked)}
              aria-label={`Administrar ${d.nombre}`}
            />
            <span className="text-xs text-text-secondary">
              {guardando === d.deviceid
                ? 'Guardando…'
                : d.administrado
                  ? 'Administrado'
                  : 'No se toca'}
            </span>
          </label>
        </Td>
      </Tr>
    ));

  return (
    <div className="space-y-4">
      <AvisoModo modo={estado.modo} />

      <section className="rounded-lg bg-bg-elevated p-4 shadow">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1 text-sm">
            <p className="inline-flex items-center gap-1.5">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  estado.conexion_ok ? 'bg-state-success' : 'bg-state-danger'
                }`}
              />
              <span className="text-text-primary">{estado.mensaje}</span>
            </p>
            <p className="text-xs text-text-secondary">
              Cuenta:{' '}
              <span className="font-mono">{estado.cuenta ?? 'sin configurar'}</span>
              {' · '}Región: <span className="font-mono">{estado.region}</span>
              {' · '}Modo:{' '}
              <span
                className={`font-mono font-bold ${
                  estado.modo === 'real'
                    ? 'text-state-danger'
                    : estado.modo === 'simulacion'
                      ? 'text-state-warning'
                      : 'text-text-muted'
                }`}
              >
                {estado.modo}
              </span>
            </p>
            <p className="text-[11px] text-text-muted">
              {estado.consultado_at
                ? `Consultado ${estado.consultado_at}`
                : 'Sin consultar'}
            </p>
          </div>
          {onProbarConexion && (
            <Button
              variant="secondary"
              onClick={onProbarConexion}
              disabled={cargando}
            >
              {cargando ? 'Consultando…' : 'Probar conexión y actualizar'}
            </Button>
          )}
        </div>

        <p className="mt-3 text-xs text-text-secondary">
          Las credenciales no se editan acá: viven en variables de entorno del
          servidor. Para cambiarlas, ver{' '}
          <code className="font-mono">docs/dev/ewelink-variables-entorno.md</code>.
        </p>
      </section>

      {sinClasificar.length > 0 && (
        <div className="rounded-md border border-state-warning bg-state-warning/10 p-3">
          <p className="text-sm font-medium text-state-warning">
            {sinClasificar.length} dispositivo(s) sin clasificar
          </p>
          <p className="text-xs text-text-secondary mt-0.5">
            Su nombre no corresponde a ningún acceso del barrio (
            {sinClasificar.map((d) => d.nombre).join(', ')}). Quedan sin
            administrar: ante la duda, el portal no los toca. Es lo correcto
            si son equipos como riego o iluminación.
          </p>
        </div>
      )}

      <section className="overflow-hidden rounded-lg bg-bg-elevated shadow">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-4 pb-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-text-primary">
            Dispositivos de la cuenta{' '}
            <span className="font-normal normal-case text-text-secondary">
              ({dispositivos.length})
            </span>
          </h2>
          <p className="text-xs text-text-secondary">
            {administrados} administrado(s)
            {capacidad > 0 && ` · capacidad ${capacidad} vecinos`}
          </p>
        </div>

        {dispositivos.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-secondary">
            No hay dispositivos cargados. Usa “Probar conexión y actualizar”
            para traerlos de la cuenta de eWeLink.
          </div>
        ) : (
          <Table>
            <THead>
              <Th>Nombre</Th>
              <Th>Device ID</Th>
              <Th>Canal</Th>
              <Th>Compartidos</Th>
              <Th>¿Lo administra el portal?</Th>
            </THead>
            <TBody>
              {filas(fisicos)}
              {virtuales.length > 0 && (
                <Tr>
                  <Td colSpan={5}>
                    <span className="text-[11px] uppercase tracking-wide text-text-muted">
                      Virtuales — absorben el desborde cuando un físico llega
                      a su tope
                    </span>
                  </Td>
                </Tr>
              )}
              {filas(virtuales)}
            </TBody>
          </Table>
        )}
      </section>
    </div>
  );
}
